import MaintenanceBand from "@assets/icons/maintenance-band.svg";
import MaintenanceClock from "@assets/icons/maintenance-clock.svg";

import useSystemStore from "@store/useSystemStore";
import { MAINTENANCE_STRINGS } from "@config/messages";
import { getLocalTimeZone } from "@utils/date.util";
import { formatWindowParts, getTimeZoneAbbreviation } from "@utils/maintenance.util";
import type { MaintenanceWindow } from "@app-types/maintenance.type";
import { MainLogo } from "@ui/Logos/MainLogo";
import { Paragraph } from "@ui/Content";
import { H3 } from "@ui/Headings";

function composeLabel(base: string, abbreviation: string): string {
  return abbreviation ? `${base}, ${abbreviation}` : base;
}

// Only the times are emphasised; the dates and "to" read back from them.
const TIME_CLASSNAME = "font-semibold text-content-primary dark:text-gray-100";

function TimeBlock({
  label,
  window,
  timeZone,
  className,
}: {
  label: string;
  window: MaintenanceWindow;
  timeZone: string;
  className?: string;
}) {
  const { startDate, startTime, endDate, endTime } = formatWindowParts(window, timeZone);

  return (
    <div className={className}>
      <p className="text-[11px] font-medium uppercase tracking-wide text-content-secondary dark:text-gray-400">
        {label}
      </p>
      {/* Neither endpoint may break internally; the end drops to its own line when needed. */}
      <p className="mt-1 text-lg text-content-secondary dark:text-gray-400">
        <span className="whitespace-nowrap">
          {startDate ? `${startDate}, ` : ""}
          <span className={TIME_CLASSNAME}>{startTime}</span> to
        </span>{" "}
        <span className="whitespace-nowrap">
          {endDate ? `${endDate}, ` : ""}
          <span className={TIME_CLASSNAME}>{endTime}</span>
        </span>
      </p>
    </div>
  );
}

function MaintenanceWindowCard({ window }: { window: MaintenanceWindow }) {
  const orgZone = window.organizationTimeZone;
  const localZone = getLocalTimeZone();

  const orgLabel = composeLabel(
    MAINTENANCE_STRINGS.WINDOW_LABEL,
    getTimeZoneAbbreviation(orgZone, window.startsAt)
  );
  const localLabel = composeLabel(
    MAINTENANCE_STRINGS.YOUR_TIME_LABEL,
    getTimeZoneAbbreviation(localZone, window.startsAt)
  );

  return (
    <div className="w-full max-w-[288px] rounded-2xl bg-[#F9F9FB] p-3 dark:border dark:border-gray-750/60 dark:bg-[#1D2026]">
      <TimeBlock
        label={orgLabel}
        window={window}
        timeZone={orgZone}
        className="px-3 py-2 text-center"
      />

      <div className="mt-2 rounded-xl bg-white p-3 text-center shadow-sm dark:bg-[#131518] dark:shadow-none">
        <TimeBlock label={localLabel} window={window} timeZone={localZone} />
      </div>
    </div>
  );
}

export default function MaintenanceScreen() {
  const { maintenanceWindow, maintenanceMessage } = useSystemStore();

  return (
    <div className="flex min-h-screen flex-col bg-white animate-fade-in dark:bg-gray-800">
      <div className="flex flex-1 flex-col items-center justify-center py-[6vh]">
        <MainLogo className="dark:text-white" />

        <div className="relative mt-8 h-[132px] w-full">
          <MaintenanceBand className="absolute left-0 top-1/2 h-auto w-full -translate-y-1/2" />
          <MaintenanceClock className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" />
        </div>

        <div className="mt-8 flex flex-col items-center px-6 text-center">
          <H3 className="text-teal-600 dark:text-white">{MAINTENANCE_STRINGS.SCREEN_TITLE}</H3>

          <Paragraph className="mt-3 max-w-[280px] text-content-secondary">
            {maintenanceMessage || MAINTENANCE_STRINGS.SCREEN_BODY}
          </Paragraph>

          {maintenanceWindow && (
            <div className="mt-10 flex w-full justify-center">
              <MaintenanceWindowCard window={maintenanceWindow} />
            </div>
          )}
        </div>
      </div>

      <footer className="shrink-0 py-4 text-center text-[11px] font-normal text-gray-450 dark:text-gray-100">
        © 2026 Meridian AI Inc. All rights reserved
      </footer>
    </div>
  );
}
